// Mass Storage bolt-hardware counting — MS's own isolated lane.
//
// This was split out of mms-utils/hardware-counting-utils.ts because MS and MMS
// requirements have diverged (shelf spacing, shelf rules, packaging) and no longer
// share placement/counting logic. Keeping MS here means MS rules can change without
// any risk to MMS, and vice-versa.
//
// MS has NO 100" unit, so there is deliberately no shelf_five / 100"-height logic here.
//
// Shelf placement is owned entirely by assignShelfPlacement (ms-shelf-utils.ts); this
// module only consumes the { hardwareSlotName, shelfType } data it produces. Bolt
// sharing between adjacent units is detected by matching hardwareSlotName across units,
// so callers MUST derive every unit's shelf data from assignShelfPlacement — base units
// included — or the position strings desync and sharing is mis-counted.

export type MsShelfData = {
  hardwareSlotName: string
  shelfType: string | undefined
}

export type MsUnitShelfData = {
  unit: number
  unitHeight: string
  shelves: MsShelfData[]
  isEndUnit?: boolean
  adjacentToCorner?: boolean
}

type FlatShelf = {
  unitIndex: number
  hardwareSlotName: string
  shelfType: string | undefined
}

// Flattens every unit's shelves into one list, tagged with their unit index.
function flattenUnits(allUnitsShelves: MsUnitShelfData[]): FlatShelf[] {
  const resolved: FlatShelf[] = []

  allUnitsShelves.forEach((unit) => {
    unit.shelves.forEach((shelf) => {
      resolved.push({
        unitIndex        : unit.unit,
        hardwareSlotName : shelf.hardwareSlotName,
        shelfType        : shelf.shelfType,
      })
    })
  })

  return resolved
}

// Returns the bolt-kit count as { shared, solo }, each already expressed in 4-bolt kits.
// A shelf that lines up with a shelf at the same hardwareSlotName in an adjacent unit
// shares its inner bolts; end shelves and corner-neighbours contribute solo bolts.
export function getMsHardwareCount(allUnitsShelves: MsUnitShelfData[]): { shared: number; solo: number } {
  const flat = flattenUnits(allUnitsShelves)

  const firstUnit = flat[0]?.unitIndex
  const lastUnitIndex = flat[flat.length - 1]?.unitIndex

  let sharedBoltCount = 0
  let soloBoltCount = 0

  for (let i = 0; i < flat.length; i += 1) {
    const thisShelf = flat[i]

    const thisUnitShelves = flat.filter(s => s?.unitIndex === thisShelf?.unitIndex)
    const previousUnitShelves = i > 0 ? flat.filter(s => s?.unitIndex === thisShelf?.unitIndex - 1) : undefined
    const nextUnitShelves = flat.filter(s => s?.unitIndex === thisShelf?.unitIndex + 1)

    // determine if this is an end shelf
    const isEndUnit = firstUnit === thisUnitShelves[0]?.unitIndex || lastUnitIndex === thisUnitShelves[0]?.unitIndex
    const hasCornerNeighbor = nextUnitShelves[0]?.shelfType === 'corner' || previousUnitShelves?.[0]?.shelfType === 'corner'

    let shelfBoltsCount = 4

    // Rail crossmembers get their own dedicated hardware and never share bolts.
    if (thisShelf.shelfType === 'rail') {
      soloBoltCount += shelfBoltsCount
      continue
    }

    // MS corner shelves can share hardware, so they add no dedicated bolts unlike MMS
    if (thisShelf.shelfType === 'corner') {
      continue
    }

    if (isEndUnit || hasCornerNeighbor) {
      shelfBoltsCount -= 2
      soloBoltCount += 2
    }

    // A rail neighbor never shares, so it must not satisfy another shelf's shared match either.
    const hasNextSharedShelf = nextUnitShelves?.find(s => s.hardwareSlotName === thisShelf.hardwareSlotName && s.shelfType !== 'rail')
    const hasPreviousSharedShelf = previousUnitShelves?.find(s => s.hardwareSlotName === thisShelf.hardwareSlotName && s.shelfType !== 'rail')

    // Dumbbell shelves share fully with any matching-position neighbor, same as every
    // other non-rail/non-corner shelf type — there is no dumbbell-to-dumbbell-only
    // restriction on the shared upright.
    if (hasNextSharedShelf) {
      shelfBoltsCount -= 2
      sharedBoltCount += 2
    }

    if (shelfBoltsCount > 0 && hasPreviousSharedShelf) {
      shelfBoltsCount -= 2
      sharedBoltCount += 2
    }

    if (shelfBoltsCount > 0) {
      soloBoltCount += shelfBoltsCount
    }
  }

  return {
    shared : Math.ceil((sharedBoltCount / 2) / 4),
    solo   : Math.ceil(soloBoltCount / 4),
  }
}
